import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/toast';
import { cn } from '../../../lib/utils';
import { usePlayerParam } from '../anniversary/playerUrl';
import IssuedBonuses from './IssuedBonuses';
import IssuedStats from './IssuedStats';
import { LISTED_TABS, tabSearch } from './issuedFilters';
import CheckResult from './CheckResult';
import { proposalSummary } from './format';
import { editStateFromSaved, issueBody, overrideBody } from './issue';
import { useViewMode } from './viewMode';

const noException = { granted: false, reason: '' };
const noOverrides = { wagering: null, amount: null };

const MODES = [
  { key: 'simple', label: 'Simple' },
  { key: 'detailed', label: 'Detailed' },
];

// The page's tabs; "issued" and "stats" live in the URL (?tab=issued, plus their filters) so they
// can be linked.
const TABS = [
  { key: 'check', label: 'Check player' },
  { key: 'issued', label: 'Issued bonuses' },
  { key: 'stats', label: 'Stats' },
];

const DESCRIPTIONS = {
  check: 'Look up a player who asked for a VIP comp. Checks run in order and stop at the first failure.',
  issued: 'Bonuses saved for issuing, newest first. A pending one can still be cancelled.',
  stats: 'How much is given away: per day, per VIP manager and per player, in USD at the issue-time rate.',
};


function Tabs({ tab, onChange }) {
  return (
    <nav aria-label="Bonus requests" className="flex gap-1 border-b border-white/10">
      {TABS.map((t) => (
        <button
          key={t.key} type="button" aria-current={tab === t.key ? 'page' : undefined} onClick={() => onChange(t.key)}
          className={cn('-mb-px border-b-2 px-4 py-2.5 text-sm font-bold',
            tab === t.key ? 'border-cta text-ink' : 'border-transparent text-ink-dim hover:text-ink')}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}

function ViewToggle({ mode, onChange }) {
  return (
    <nav aria-label="View mode" className="flex rounded-xl border border-white/10 bg-surface-1 p-1">
      {MODES.map(({ key, label }) => (
        <button
          key={key} type="button" aria-pressed={mode === key} onClick={() => onChange(key)}
          className={cn('flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold',
            mode === key ? 'bg-ink text-surface' : 'text-ink-dim hover:text-ink')}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

export default function BonusRequestsPage() {
  const { activeBrand } = useBrand();
  const { show, ToastComponent } = useToast();
  const [player, setPlayer] = usePlayerParam();
  const [searchParams, setSearchParams] = useSearchParams();
  // A player being checked always shows the check tab (a row of the issued list opens one).
  const urlTab = searchParams.get('tab');
  const tab = !player && LISTED_TABS.includes(urlTab) ? urlTab : 'check';
  const setTab = (next) => {
    if (LISTED_TABS.includes(next)) setPlayer('');
    // this URL update runs last, so it wins over setPlayer's: user_id is dropped here too.
    setSearchParams((sp) => tabSearch(sp, next), { replace: true });
  };
  const [text, setText] = useState(player);
  const [check, setCheck] = useState(null);
  const [exception, setException] = useState(noException);
  const [overrides, setOverrides] = useState(noOverrides);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [mode, setMode] = useViewMode();
  const [error, setError] = useState('');

  // Only the latest search may update state, so a slow response for an earlier player or brand
  // cannot overwrite the current one.
  const requestIdRef = useRef(0);

  const run = useCallback(async (q) => {
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestId === requestIdRef.current;
    setLoading(true);
    setError('');
    setCheck(null);
    setException(noException);
    setOverrides(noOverrides);
    try {
      const res = await vipAPI.getBonusRequestCheck(q);
      if (isLatest()) {
        // a pending comp is corrected from what was saved (amount, wagering, exception grant)
        const saved = editStateFromSaved(res.data);
        setCheck(res.data);
        setOverrides(saved.overrides);
        setException(saved.exception);
      }
    } catch (e) {
      if (isLatest()) setError(e?.response?.data?.error || 'Checking the player failed.');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, []);

  // The box follows the URL's player; a brand switch keeps whatever is being typed.
  useEffect(() => { setText(player); }, [player]);

  // A user ID in the URL (shared link, back/forward) or a brand switch re-runs the check.
  useEffect(() => {
    if (player) {
      run(player);
    } else {
      requestIdRef.current += 1;
      setCheck(null);
      setError('');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, activeBrand]);

  const submit = (e) => {
    e.preventDefault();
    const q = text.trim();
    if (!q) return;
    if (q === player) run(q);
    else setPlayer(q); // the effect above runs the check
  };

  const copyText = async (text, done) => {
    try {
      await navigator.clipboard.writeText(text);
      show(done, 'success');
    } catch {
      show('Copying failed.', 'error');
    }
  };
  const copy = () => copyText(proposalSummary(check, exception, overrides), 'Summary copied.');

  // issue saves the comp for issuing; the backend re-runs the checks, so a refused one says why.
  // cancel withdraws the pending comp; the card then shows who cancelled it.
  const cancel = async (id) => {
    const requestId = requestIdRef.current;
    setCancelling(true);
    try {
      const res = await vipAPI.cancelBonus(id);
      if (requestId === requestIdRef.current) setCheck((c) => ({ ...c, issued: res.data }));
      show('Bonus cancelled.', 'success');
      return true;
    } catch (e) {
      show(e?.response?.data?.error || 'Cancelling the bonus failed.', 'error');
      return false;
    } finally {
      setCancelling(false);
    }
  };

  // override: the form for issuing although a check failed; null for the normal comp.
  const issue = async (comment, override) => {
    const requestId = requestIdRef.current;
    setIssuing(true);
    try {
      const body = override ? overrideBody(check, override) : issueBody(check, exception, overrides, comment);
      const res = await vipAPI.issueBonus(body);
      if (requestId === requestIdRef.current) setCheck((c) => ({ ...c, issued: res.data }));
      show(check.issued?.status === 'pending' ? 'Bonus updated.' : 'Bonus queued for issuing.', 'success');
      return true;
    } catch (e) {
      show(e?.response?.data?.error || 'Issuing the bonus failed.', 'error');
      return false;
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-black text-ink">Bonus Requests</h1>
          <p className="mt-1 text-sm font-medium text-ink-dim">
            {DESCRIPTIONS[tab]}
          </p>
        </div>
        {tab === 'check' && <ViewToggle mode={mode} onChange={setMode} />}
      </div>

      <Tabs tab={tab} onChange={setTab} />

      {tab === 'issued' && <IssuedBonuses onOpenPlayer={setPlayer} notify={show} />}
      {tab === 'stats' && <IssuedStats onOpenPlayer={setPlayer} />}

      {tab === 'check' && (
        <>
          <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="User ID or email"
              aria-label="User ID or email"
              className="h-11 max-w-md flex-1"
            />
            <Button type="submit" disabled={loading || !text.trim()} className="h-11 gap-2 px-5">
              <Search className="h-4 w-4" /> Check
            </Button>
          </form>

          {loading && <div className="flex justify-center py-10"><Spinner /></div>}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
          {check && !loading && (
            <CheckResult
              check={check} detailed={mode === 'detailed'}
              exception={exception} setException={setException} overrides={overrides} setOverrides={setOverrides}
              onCopy={copy} onIssue={issue} issuing={issuing} onCancel={cancel} cancelling={cancelling}
              onShowDetails={() => setMode('detailed')} onRecheck={() => run(player || text.trim())}
            />
          )}
          {!check && !loading && !error && (
            <div className="rounded-xl border border-dashed border-white/10 px-6 py-10 text-center text-sm font-medium text-ink-dim">
              Enter a user ID or email and press Check to run the eligibility checks.
            </div>
          )}
        </>
      )}
      {ToastComponent}
    </div>
  );
}
